import { TABLET_SCREEN_SIZE } from "@/components/AppNavigation/constants";
import { EmptyBagMessageCard } from "@/components/Checkout/EmptyBagMessageCard";
import LandingPageWrapper from "@/components/pages/AppPages/Pages/components/LandingPageWrapper";
import { BrandclubTermsAndConditions } from "@/components/pages/Auth/TermsAndConditions";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { getActiveCarts } from "@/redux/reducers/checkout/thunk";
import { findScrollableParent } from "@/utils/pageUtils";
import { formatPhone, getEntityImage } from "@/utils/StringUtils";
import { styled, useMediaQuery } from "@mui/material";
import { useEffect, useRef } from "react";
import { Divider } from "../../Divider";
import { CheckoutContext } from "../checkoutUIMachine";
import { CheckoutUIProvider, useCheckoutUI } from "../CheckoutUIProvider";
import { useAsyncCheckoutEnabled } from "../hooks/useAsyncCheckoutEnabled";
import { useCheckoutRetailer } from "../hooks/useCheckoutRetailer";
import { CartTotal } from "./components/CartTotal";
import { CheckoutPaymentEditForm } from "./components/CheckoutPaymentEditForm";
import CollapsibleSection, {
  SectionTitle,
} from "./components/CollapsibleSection";
import { CurrentCart } from "./components/CurrentCart";
import DebugPanel from "./components/DebugPanel";
import DesktopCheckoutSignIn from "./components/DesktopCheckoutSignIn";
import { RetailerErrorAndTerms } from "./components/ErrorAndTerms";
import { MobileCart } from "./components/MobileCart";
import MobileCheckoutSignIn from "./components/MobileCheckoutSignIn";
import { PaymentMethodDisplay } from "./components/PaymentMethodDisplay";
import { RetailerLoginForm } from "./components/RetailerLoginForm";
import { ShippingMethodForm } from "./components/ShippingMethodForm";
import { SubmitCheckoutButton } from "./components/SubmitCheckoutButton";
import { SuccessStatusIcon } from "./components/SuccessStatusIcon";
import { useLoadingCheckoutIntent } from "../hooks/useLoadingCheckoutIntent";
import { useAddToCartFromQueryParams } from "@/components/Checkout/useAutoAddToBag";
import Loading from "@/components/Loading";

const StyledEmptyBagMessageCard = styled(EmptyBagMessageCard)({
  margin: "auto",
  maxHeight: 250,
  maxWidth: 500,
});

const Container = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  width: "100%",
  height: "100%",
  gap: "148px",
  ".title": {
    fontWeight: "bold",
    fontSize: 26,
    paddingBottom: 22.1,
    [theme.breakpoints.down("md")]: {
      fontSize: 22,
      paddingBottom: 4,
    },
  },
  ".content": {
    flex: 1,
    [theme.breakpoints.down("md")]: {
      flex: "none",
    },
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  ".sidebar": {
    width: "430px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  ".mobileOnly": {
    display: "none",
  },
  [theme.breakpoints.up("md")]: {
    marginTop: "68px",
  },
  // Responsive styles for tablet and mobile
  [theme.breakpoints.down("md")]: {
    marginTop: "24px",
    flexDirection: "column",
    gap: "30px",
    ".sidebar": {
      width: "100%",
      display: "none", // Hide the regular sidebar on mobile
    },
    ".mobileOnly": {
      display: "block",
    },
    ".mobileTotalAndButton": {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      paddingBottom: "24px",
    },
  },
}));

const CheckoutFormInner = () => {
  const { currentRetailer } = useCheckoutRetailer();
  const userProfile = useAppSelector((state) => state.userProfile);
  const { debug } = useAsyncCheckoutEnabled();

  const shippingRef = useRef<HTMLDivElement>(null);
  const paymentRef = useRef<HTMLDivElement>(null);
  const retailerLoginRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery(`(max-width:${TABLET_SCREEN_SIZE}px)`);
  const dispatch = useAppDispatch();

  const [state, send] = useCheckoutUI();

  const {
    signIn,
    shipping,
    payment,
    retailerLogin,
    checkoutIntent,
    shippingInput,
  } = state.context;

  const scrollToSection = (ref: React.RefObject<HTMLDivElement> | null) => {
    if (ref && ref.current) {
      const scrollableParent = findScrollableParent(ref.current);
      if (scrollableParent) {
        const parentRect = scrollableParent.getBoundingClientRect();
        const elementRect = ref.current.getBoundingClientRect();
        const topOffset = 64;

        const scrollTop =
          scrollableParent.scrollTop +
          (elementRect.top - parentRect.top) -
          topOffset;

        scrollableParent.scrollTo({
          top: scrollTop,
          behavior: "smooth",
        });
      }
    }
  };

  const { loading } = useLoadingCheckoutIntent();

  useEffect(() => {
    dispatch(getActiveCarts());
    if (userProfile?.userId && state.value === "initial") {
      send({
        type: "SET_USER_ID",
        data: { userId: userProfile?.userId },
      });
    }
  }, [userProfile?.userId, send, state.value, dispatch]);

  const toggleSection = (
    step: keyof Pick<CheckoutContext, "shipping" | "payment" | "retailerLogin">
  ) => {
    send({ type: "TOGGLE", data: { section: step } });
  };

  const shippingOpen = state.context["shipping"].open;
  const paymentOpen = state.context["payment"].open;
  const retailerLoginOpen = state.context["retailerLogin"].open;

  const showEnabledSubmitButton =
    state.value === "ready_to_checkout" || state.value === "checking_out";

  useEffect(() => {
    if (shippingOpen) {
      scrollToSection(shippingRef);
    }
  }, [shippingOpen]);

  useEffect(() => {
    if (paymentOpen) {
      scrollToSection(paymentRef);
    }
  }, [paymentOpen]);

  useEffect(() => {
    if (retailerLoginOpen) {
      scrollToSection(retailerLoginRef);
    }
  }, [retailerLoginOpen]);

  if (checkoutIntent?.items?.length === 0) {
    return (
      <LandingPageWrapper includeFooter={false}>
        <StyledEmptyBagMessageCard />
      </LandingPageWrapper>
    );
  }

  return (
    <LandingPageWrapper includeFooter={false}>
      <Container className="checkout-container">
        {/* Content Section */}
        <div className="content">
          <div className="title">Checkout</div>

          {/* Sign In */}
          <CollapsibleSection
            isOpen={signIn.open}
            title={"Account verification"}
            titleIcon={
              userProfile?.userId ? (
                <SuccessStatusIcon success={!!userProfile?.userId} />
              ) : null
            }
            hideControl={!!userProfile?.userId}
            collapsedContent={
              <div style={{ paddingTop: 8 }}>
                <div>{formatPhone(userProfile?.phoneNumber!)}</div>
              </div>
            }
          >
            {/* If user is signed in, show it expanded but no handle and don't let them toggle it */}
            {isMobile ? (
              <MobileCheckoutSignIn />
            ) : (
              <div style={{ paddingTop: 16 }}>
                <DesktopCheckoutSignIn />
              </div>
            )}
          </CollapsibleSection>

          <Divider />

          {/* Payment */}
          {payment.visible && (
            <>
              <div ref={paymentRef}>
                <CollapsibleSection
                  isOpen={payment.open}
                  loading={loading}
                  disabled={!payment.canOpen}
                  onToggle={() => {
                    toggleSection("payment");
                  }}
                  title={"Payment method"}
                  titleIcon={
                    checkoutIntent?.stripePaymentMethod ? (
                      <SuccessStatusIcon success={!!userProfile?.userId} />
                    ) : null
                  }
                  collapsedContent={
                    checkoutIntent?.stripePaymentMethod ? (
                      <PaymentMethodDisplay
                        paymentMethod={checkoutIntent.stripePaymentMethod}
                      />
                    ) : (
                      "Enter your preferred payment method"
                    )
                  }
                  style={{
                    opacity:
                      checkoutIntent?.stripePaymentMethod || payment.open
                        ? 1
                        : 0.5,
                  }}
                >
                  <CheckoutPaymentEditForm formVisible={payment.open} />
                </CollapsibleSection>
              </div>
              <Divider />
            </>
          )}

          {/* Shipping */}
          {shipping.visible && (
            <>
              <div ref={shippingRef}>
                <CollapsibleSection
                  isOpen={shipping.open}
                  disabled={!shipping.canOpen}
                  loading={loading}
                  title={"Shipping address"}
                  titleIcon={
                    !shipping.forceComplete ? (
                      <SuccessStatusIcon success={!!userProfile?.userId} />
                    ) : null
                  }
                  onToggle={() => {
                    toggleSection("shipping");
                  }}
                  style={{
                    opacity: shipping.open || shipping.canOpen ? 1 : 0.5,
                  }}
                  collapsedContent={
                    shipping.forceComplete ? (
                      "Enter your preferred shipping address"
                    ) : (
                      <div>
                        <div>
                          {shippingInput?.firstName} {shippingInput?.lastName}
                        </div>
                        <div>
                          {shippingInput?.address1}, {shippingInput?.address2}
                          {shippingInput?.address2 ? "," : ""}{" "}
                          {shippingInput?.city}, {shippingInput?.state}{" "}
                          {shippingInput?.zipCode}
                        </div>
                      </div>
                    )
                  }
                >
                  <ShippingMethodForm />
                </CollapsibleSection>
              </div>
              <Divider />
            </>
          )}

          {/* Retailer Login */}
          {retailerLogin.visible && (
            <>
              <div ref={retailerLoginRef}>
                <CollapsibleSection
                  isOpen={retailerLogin.open}
                  disabled={!retailerLogin.canOpen}
                  loading={loading}
                  onToggle={() => {
                    toggleSection("retailerLogin");
                  }}
                  titleIcon={
                    checkoutIntent?.retailerConnection ? (
                      <SuccessStatusIcon success={!!userProfile?.userId} />
                    ) : null
                  }
                  style={{
                    opacity:
                      checkoutIntent?.retailerConnection || retailerLogin.open
                        ? 1
                        : 0.5,
                  }}
                  collapsedContent={
                    <div>
                      {checkoutIntent?.retailerConnection
                        ? checkoutIntent?.retailerConnection.username
                        : `Connect your ${
                            currentRetailer?.displayName || "retailer"
                          } account to earn rewards
and get expedited shipping`}
                    </div>
                  }
                  title={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "row",
                        gap: "8px",
                      }}
                    >
                      <img
                        src={getEntityImage(
                          currentRetailer?.retailerId!,
                          "retailer"
                        )}
                        alt="Retailer Logo"
                        style={{
                          width: "24px",
                          height: "24px",
                        }}
                      />
                      <div>
                        {`${
                          currentRetailer?.displayName || "Retailer"
                        } Account`}
                      </div>
                    </div>
                  }
                >
                  <RetailerLoginForm />
                </CollapsibleSection>
              </div>
              <Divider />
              {debug && <DebugPanel />}
            </>
          )}
        </div>
        {/* Mobile Total and Submit Button - Only shown on mobile */}
        <div className="mobileOnly mobileTotalAndButton">
          <div
            style={{
              width: "100%",
              marginBottom: "24.7px",
            }}
          >
            <SectionTitle>Order Summary</SectionTitle>
          </div>
          <MobileCart />
          {debug && <DebugPanel />}
          <RetailerErrorAndTerms />
          <div
            style={{
              width: "100%",
              marginTop: 12.4,
              marginBottom: 16,
              paddingBottom: showEnabledSubmitButton ? 64 : 0,
            }}
          >
            <CartTotal />
          </div>

          {!showEnabledSubmitButton && (
            <>
              <SubmitCheckoutButton />
              <BrandclubTermsAndConditions
                sx={{
                  fontSize: 10,
                  marginTop: "16px",
                }}
              />
            </>
          )}
        </div>
        {/* Desktop Sidebar - Hidden on mobile */}
        <div className="sidebar">
          <CurrentCart />
        </div>
        {showEnabledSubmitButton && (
          <div
            className="mobileOnly mobileTotalAndButton"
            style={{
              width: "100vw",
              boxSizing: "border-box",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: "8px",
              position: "fixed",
              bottom: 0,
              left: 0,
              padding: "16px",
              backgroundColor: "white",
              boxShadow: "0px -4px 10px rgba(0, 0, 0, 0.1)",
              zIndex: 1000,
            }}
          >
            <SubmitCheckoutButton showTotal />
            <BrandclubTermsAndConditions
              sx={{
                fontSize: 10,
                marginTop: 0,
                marginBottom: 0,
              }}
            />
          </div>
        )}
      </Container>
    </LandingPageWrapper>
  );
};

const CheckoutForm = () => {
  const { retailerId, retailerSku, stacklineSku } =
    useAddToCartFromQueryParams();
  if (retailerId && retailerSku && stacklineSku) {
    return (
      <div>
        <Loading fullscreen ellipsis />
      </div>
    );
  }
  return (
    <CheckoutUIProvider>
      <CheckoutFormInner />
    </CheckoutUIProvider>
  );
};

export default CheckoutForm;
